import { IProduct } from '@models/IProduct';
import { IProductListingStatusLabels } from '@models/IProductListingStatusLabels';
import KitDropdown from '@molecules/KitDropdown/KitDropdown';
import ProductListingCard from '@molecules/ProductListingCard/ProductListingCard';
import chunkArray from '@utils/helpers/chunkArray';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';

interface Props {
  readonly hidePrices: boolean;
  readonly products: IProduct[];
  readonly productsStatusLabels: IProductListingStatusLabels;
}

const SearchSummaryProductsRow = ({
  hidePrices,
  products,
  productsStatusLabels,
}: Props) => {
  const isMobile = useMediaQuery(1024);
  const [activeProduct, setActiveProduct] = useState<IProduct | null>(null);
  const [activeProductRowIndex, setActiveProductRowIndex] = useState<
    number | null
  >(null);
  const [activeProductColIndex, setActiveProductColIndex] = useState<
    number | null
  >(null);
  const displayedProducts = chunkArray(products, isMobile ? 1 : 3);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Additional Dependencies
  useEffect(() => {
    setActiveProduct(null);
    setActiveProductRowIndex(null);
    setActiveProductColIndex(null);
  }, [isMobile]);

  return (
    <div
      className="flex flex-col gap-7.5 lg:gap-4"
      data-testid="CT-48-Search-Summary-Results-Row-Products"
    >
      {displayedProducts.map((productsRow, rowIndex) => (
        <div key={productsRow.map((product) => product.title).join('-')}>
          <div className="container lg:grid lg:grid-cols-3 lg:gap-4">
            {productsRow.map((product, colIndex) => {
              const isActiveProduct =
                activeProductRowIndex === rowIndex &&
                activeProductColIndex === colIndex;

              return (
                <ProductListingCard
                  active={isActiveProduct}
                  dataTestId="CT-48-Search-Summary-Results-Row-Products"
                  hidePrices={hidePrices}
                  isLightMode
                  key={`product-${product.title}`}
                  statusLabels={productsStatusLabels!}
                  onKitDropdownButtonClick={() => {
                    setActiveProduct(isActiveProduct ? null : product);
                    setActiveProductRowIndex(isActiveProduct ? null : rowIndex);
                    setActiveProductColIndex(isActiveProduct ? null : colIndex);
                  }}
                  {...product}
                />
              );
            })}
          </div>

          <AnimatePresence mode="wait">
            {activeProduct && rowIndex === activeProductRowIndex && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
              >
                {activeProduct.variations && (
                  <KitDropdown
                    hidePrices={hidePrices}
                    isLightMode
                    kitTitleLabel={activeProduct.kitTitleLabel}
                    kitsTitleLabel={activeProduct.kitsTitleLabel}
                    statusLabels={productsStatusLabels}
                    variations={activeProduct.variations}
                  />
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

export default SearchSummaryProductsRow;
